import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import './Admin.css';
import { LockIcon, SettingsIcon, CreditCardIcon, TagIcon, UsersIcon, StoreIcon, TruckIcon, MailIcon, DollarIcon, BankIcon, TrashIcon } from '../../components/SvgIcons';
import AedPrice from '../../components/AedPrice';

const SETTINGS_PIN = '4444';

const Settings = () => {
  const { token } = useAuth();
  const { language, isRTL } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // PIN protection
  const [pinVerified, setPinVerified] = useState(false);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');

  // Active tab
  const [activeTab, setActiveTab] = useState('general');

  // Store visibility
  const [storeOpen, setStoreOpen] = useState(true);

  // Payment methods
  const [codEnabled, setCodEnabled] = useState(true);
  const [bankTransferEnabled, setBankTransferEnabled] = useState(true);
  const [creditCardEnabled, setCreditCardEnabled] = useState(false);

  // Delivery fee options
  const [forcePayToDriver, setForcePayToDriver] = useState(false);
  const [deliveryFeeOptions, setDeliveryFeeOptions] = useState([30, 50, 0]);
  const [newFeeValue, setNewFeeValue] = useState('');
  const [editingFeeIndex, setEditingFeeIndex] = useState(null);
  const [editingFeeValue, setEditingFeeValue] = useState('');

  // Show email on pay page
  const [showEmailOnPayPage, setShowEmailOnPayPage] = useState(false);

  // Email language
  const [emailLanguage, setEmailLanguage] = useState('ar');

  // WhatsApp template
  const defaultWhatsAppTemplate = `السلام عليكم،\n\nشكراً لطلبكم المنتجات الخالية من الغلوتين\n\nالاسم: {customerName}\nرقم الطلب: {orderId}\nالمبلغ الإجمالي: {total} درهم شامل التوصيل.\n\nيرجى إكمال الطلب عبر الرابط التالي:\n{paymentLink}`;
  const [whatsappTemplate, setWhatsappTemplate] = useState(defaultWhatsAppTemplate);

  // WhatsApp template for Pay to Driver orders
  const defaultWhatsAppDriverTemplate = `السلام عليكم،\n\nشكراً لطلبكم المنتجات الخالية من الغلوتين\n\nالاسم: {customerName}\nرقم الطلب: {orderId}\nالمبلغ الإجمالي: {total} درهم\n\nيرجى دفع رسوم التوصيل {deliveryFee} درهم نقداً للسائق عند الاستلام\nPlease pay delivery fee AED {deliveryFee} cash to the driver\n\nيرجى إكمال الطلب عبر الرابط التالي:\n{paymentLink}`;
  const [whatsappDriverTemplate, setWhatsappDriverTemplate] = useState(defaultWhatsAppDriverTemplate);

  // Email templates
  const defaultEmailTemplates = {
    confirmTitleAr: 'تم تأكيد طلبك ✓',
    confirmTitleEn: 'Order Confirmed! ✓',
    confirmSubtitleAr: 'شكراً لطلبك',
    confirmSubtitleEn: 'Thank you for your order',
    confirmDisclaimerAr: 'تم استلام طلبك. سنقوم بمعالجة وشحن طلبك بعد تأكيد الدفع. في حال عدم توفر أي منتج بعد تأكيد الدفع، سيتم استرداد المبلغ.',
    confirmDisclaimerEn: 'Your order request has been received. We will process and ship your items once payment is confirmed. If any items become unavailable after payment confirmation, you will be refunded.',
    shippedTitleAr: 'تم شحن طلبك! 🚚',
    shippedTitleEn: 'Your Order Has Been Shipped! 🚚',
    footerText: 'Web.ae Shop'
  };
  const [emailTemplates, setEmailTemplates] = useState({ ...defaultEmailTemplates });
  const [emailTemplateTab, setEmailTemplateTab] = useState('confirm');

  // Bank details
  const [bankDetails, setBankDetails] = useState({
    bankName: '',
    bankNameAr: '',
    accountName: '',
    accountNameAr: '',
    iban: '',
    accountNumber: ''
  });

  // Promo codes
  const [promoCodes, setPromoCodes] = useState([]);
  const [loadingPromos, setLoadingPromos] = useState(false);
  const [showAddPromo, setShowAddPromo] = useState(false);
  const [newPromo, setNewPromo] = useState({ code: '', discountType: 'percentage', discountValue: '', maxDiscount: '', minOrder: '', maxUses: '', expiresAt: '' });
  const [promoMessage, setPromoMessage] = useState({ type: '', text: '' });

  // User management
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserData, setEditUserData] = useState({ name: '', email: '', password: '', role: '' });
  const [userMessage, setUserMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (pinVerified) {
      fetchSettings();
      fetchUsers();
      fetchPromoCodes();
    }
  }, [pinVerified]);

  const handlePinSubmit = (e) => {
    e.preventDefault();
    if (pin === SETTINGS_PIN) {
      setPinVerified(true);
      setPinError('');
    } else {
      setPinError(language === 'ar' ? 'رمز PIN غير صحيح' : 'Incorrect PIN');
      setPin('');
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch('/api/settings', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const settings = await response.json();
        setStoreOpen(settings.storeOpen !== 'false');
        setCodEnabled(settings.codEnabled !== 'false');
        setBankTransferEnabled(settings.bankTransferEnabled !== 'false');
        setCreditCardEnabled(settings.creditCardEnabled === 'true');
        if (settings.deliveryFeeOptions) {
          try {
            const opts = JSON.parse(settings.deliveryFeeOptions);
            if (Array.isArray(opts) && opts.length > 0) setDeliveryFeeOptions(opts.map(Number));
          } catch(e) {}
        } else if (settings.deliveryFee) {
          setDeliveryFeeOptions([Number(settings.deliveryFee) || 30]);
        }
        setForcePayToDriver(settings.forcePayToDriver === 'true');
        setShowEmailOnPayPage(settings.showEmailOnPayPage === 'true');
        setEmailLanguage(settings.emailLanguage || 'ar');
        if (settings.whatsappTemplate) setWhatsappTemplate(settings.whatsappTemplate);
        if (settings.whatsappDriverTemplate) setWhatsappDriverTemplate(settings.whatsappDriverTemplate);
        if (settings.emailTemplates) {
          try {
            const saved = JSON.parse(settings.emailTemplates);
            setEmailTemplates(prev => ({ ...prev, ...saved }));
          } catch (e) {}
        }
        setBankDetails({
          bankName: settings.bankName || 'Emirates NBD',
          bankNameAr: settings.bankNameAr || 'الإمارات دبي الوطني',
          accountName: settings.accountName || 'Company Name LLC',
          accountNameAr: settings.accountNameAr || 'اسم الشركة ذ.م.م',
          iban: settings.iban || 'AE12 0260 0010 1234 5678 901',
          accountNumber: settings.accountNumber || '1234567890'
        });
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (key, value) => {
    try {
      const response = await fetch(`/api/settings/${key}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ value: String(value) })
      });
      return response.ok;
    } catch (error) {
      console.error('Failed to update setting:', error);
      return false;
    }
  };

  const showMsg = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleToggle = async (setting, currentValue, setter) => {
    const newValue = !currentValue;
    setter(newValue);
    const success = await updateSetting(setting, newValue);
    if (!success) {
      setter(currentValue);
      showMsg('error', language === 'ar' ? 'فشل في حفظ الإعداد' : 'Failed to save setting');
    }
  };

  const saveDeliveryFeeOptions = async () => {
    if (deliveryFeeOptions.length === 0) {
      showMsg('error', language === 'ar' ? 'أضف خيار توصيل واحد على الأقل' : 'Add at least one delivery option');
      return;
    }
    const success = await updateSetting('deliveryFeeOptions', JSON.stringify(deliveryFeeOptions));
    // Also save the first option as the default deliveryFee for backward compatibility
    if (success) await updateSetting('deliveryFee', String(deliveryFeeOptions[0]));
    showMsg(success ? 'success' : 'error',
      success
        ? (language === 'ar' ? 'تم حفظ خيارات التوصيل' : 'Delivery options saved')
        : (language === 'ar' ? 'فشل في حفظ خيارات التوصيل' : 'Failed to save delivery options')
    );
  };

  const addFeeOption = () => {
    const val = parseFloat(newFeeValue);
    if (isNaN(val) || val < 0) return;
    if (deliveryFeeOptions.includes(val)) {
      showMsg('error', language === 'ar' ? 'هذا الخيار موجود بالفعل' : 'This option already exists');
      return;
    }
    setDeliveryFeeOptions(prev => [...prev, val]);
    setNewFeeValue('');
  };

  const removeFeeOption = (index) => {
    setDeliveryFeeOptions(prev => prev.filter((_, i) => i !== index));
  };

  const startEditFee = (index) => {
    setEditingFeeIndex(index);
    setEditingFeeValue(String(deliveryFeeOptions[index]));
  };

  const saveEditFee = () => {
    const val = parseFloat(editingFeeValue);
    if (isNaN(val) || val < 0) return;
    setDeliveryFeeOptions(prev => prev.map((opt, i) => i === editingFeeIndex ? val : opt));
    setEditingFeeIndex(null);
    setEditingFeeValue('');
  };

  const handleBankDetailChange = (field, value) => {
    setBankDetails(prev => ({ ...prev, [field]: value }));
  };

  const saveBankDetails = async () => {
    setSaving(true);
    try {
      const updates = [
        updateSetting('bankName', bankDetails.bankName),
        updateSetting('bankNameAr', bankDetails.bankNameAr),
        updateSetting('accountName', bankDetails.accountName),
        updateSetting('accountNameAr', bankDetails.accountNameAr),
        updateSetting('iban', bankDetails.iban),
        updateSetting('accountNumber', bankDetails.accountNumber)
      ];
      const results = await Promise.all(updates);
      showMsg(
        results.every(r => r) ? 'success' : 'error',
        results.every(r => r)
          ? (language === 'ar' ? 'تم حفظ التفاصيل البنكية' : 'Bank details saved')
          : (language === 'ar' ? 'فشل في حفظ بعض الإعدادات' : 'Failed to save some settings')
      );
    } catch (error) {
      showMsg('error', language === 'ar' ? 'فشل في حفظ التفاصيل' : 'Failed to save details');
    } finally {
      setSaving(false);
    }
  };

  // Promo code functions
  const fetchPromoCodes = async () => {
    setLoadingPromos(true);
    try {
      const response = await fetch('/api/promo', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPromoCodes(data);
      }
    } catch (error) {
      console.error('Failed to fetch promo codes:', error);
    } finally {
      setLoadingPromos(false);
    }
  };

  const handleAddPromo = async () => {
    if (!newPromo.code || !newPromo.discountValue) {
      setPromoMessage({ type: 'error', text: language === 'ar' ? 'أدخل الرمز والقيمة' : 'Enter code and value' });
      return;
    }
    try {
      const response = await fetch('/api/promo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newPromo)
      });
      if (response.ok) {
        const promo = await response.json();
        setPromoCodes([promo, ...promoCodes]);
        setNewPromo({ code: '', discountType: 'percentage', discountValue: '', maxDiscount: '', minOrder: '', maxUses: '', expiresAt: '' });
        setShowAddPromo(false);
        setPromoMessage({ type: 'success', text: language === 'ar' ? 'تم إضافة رمز الخصم' : 'Promo code added' });
      } else {
        const err = await response.json();
        setPromoMessage({ type: 'error', text: err.error || 'Failed' });
      }
    } catch (error) {
      setPromoMessage({ type: 'error', text: 'Failed to add promo code' });
    }
    setTimeout(() => setPromoMessage({ type: '', text: '' }), 3000);
  };

  const togglePromoActive = async (promo) => {
    try {
      const response = await fetch(`/api/promo/${promo.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ active: !promo.active })
      });
      if (response.ok) {
        setPromoCodes(promoCodes.map(p => p.id === promo.id ? { ...p, active: !p.active } : p));
      }
    } catch (error) {
      console.error('Failed to toggle promo:', error);
    }
  };

  const deletePromo = async (id) => {
    if (!window.confirm(language === 'ar' ? 'حذف رمز الخصم؟' : 'Delete promo code?')) return;
    try {
      const response = await fetch(`/api/promo/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setPromoCodes(promoCodes.filter(p => p.id !== id));
      }
    } catch (error) {
      console.error('Failed to delete promo:', error);
    }
  };

  // User management functions
  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await fetch('/api/users', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    setEditUserData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role
    });
    setUserMessage({ type: '', text: '' });
  };

  const closeEditUser = () => {
    setEditingUser(null);
    setEditUserData({ name: '', email: '', password: '', role: '' });
    setUserMessage({ type: '', text: '' });
  };

  const saveUser = async () => {
    if (!editingUser) return;
    try {
      const updateData = {};
      if (editUserData.name !== editingUser.name) updateData.name = editUserData.name;
      if (editUserData.email !== editingUser.email) updateData.email = editUserData.email;
      if (editUserData.password) updateData.password = editUserData.password;
      if (editUserData.role !== editingUser.role) updateData.role = editUserData.role;

      if (Object.keys(updateData).length === 0) {
        setUserMessage({ type: 'info', text: language === 'ar' ? 'لا توجد تغييرات' : 'No changes made' });
        return;
      }

      const response = await fetch(`/api/users/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        const updatedUser = await response.json();
        setUsers(users.map(u => u.id === updatedUser.id ? updatedUser : u));
        setUserMessage({ type: 'success', text: language === 'ar' ? 'تم تحديث المستخدم' : 'User updated' });
        setTimeout(() => closeEditUser(), 1500);
      } else {
        const error = await response.json();
        setUserMessage({ type: 'error', text: error.error || (language === 'ar' ? 'فشل التحديث' : 'Update failed') });
      }
    } catch (error) {
      setUserMessage({ type: 'error', text: language === 'ar' ? 'فشل التحديث' : 'Update failed' });
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذا المستخدم؟' : 'Are you sure you want to delete this user?')) return;
    try {
      const response = await fetch(`/api/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setUsers(users.filter(u => u.id !== userId));
        showMsg('success', language === 'ar' ? 'تم حذف المستخدم' : 'User deleted');
      } else {
        const error = await response.json();
        showMsg('error', error.error || (language === 'ar' ? 'فشل الحذف' : 'Delete failed'));
      }
    } catch (error) {
      showMsg('error', language === 'ar' ? 'فشل الحذف' : 'Delete failed');
    }
  };

  // PIN Entry Screen
  if (!pinVerified) {
    return (
      <div className="pin-overlay">
        <div className="pin-modal">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}><LockIcon size="24px" /> {language === 'ar' ? 'إدخال رمز PIN' : 'Enter PIN'}</h2>
          <p>{language === 'ar' ? 'أدخل رمز PIN للوصول إلى الإعدادات' : 'Enter PIN to access settings'}</p>
          <form onSubmit={handlePinSubmit}>
            <input
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              value={pin}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '');
                setPin(val);
                setPinError('');
              }}
              className="form-input"
              style={{
                textAlign: 'center',
                fontSize: '24px',
                letterSpacing: '8px',
                padding: '16px',
                marginBottom: '16px'
              }}
              placeholder="••••"
              autoFocus
              autoComplete="off"
              data-lpignore="true"
              data-form-type="other"
            />
            {pinError && <div style={{ color: '#ef4444', marginBottom: '16px' }}>{pinError}</div>}
            <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>
              {language === 'ar' ? 'دخول' : 'Enter'}
            </button>
          </form>
          <Link to="/dashboard4" style={{ display: 'block', marginTop: '20px', color: 'var(--gray-500)', fontSize: '14px' }}>
            {language === 'ar' ? '← العودة للوحة التحكم' : '← Back to Dashboard'}
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container">
        <div className="loading"><div className="spinner"></div></div>
      </div>
    );
  }

  const tabs = [
    { id: 'general', icon: <SettingsIcon size="18px" />, label: language === 'ar' ? 'عام' : 'General' },
    { id: 'payment', icon: <CreditCardIcon size="18px" />, label: language === 'ar' ? 'الدفع' : 'Payment' },
    { id: 'promo', icon: <TagIcon size="18px" />, label: language === 'ar' ? 'الخصم' : 'Promos' },
    { id: 'users', icon: <UsersIcon size="18px" />, label: language === 'ar' ? 'المستخدمين' : 'Users' }
  ];

  return (
    <div className={`admin-page ${isRTL ? 'rtl' : ''}`}>
      <div className="container">
        <div className="admin-card" style={{ padding: '12px 16px', marginBottom: '24px', ...(window.innerWidth >= 768 ? { position: 'sticky', top: '72px', zIndex: 99 } : {}) }}>
          <div className="admin-nav" style={{ marginBottom: 0 }}>
            <Link to="/dashboard4">{language === 'ar' ? 'لوحة التحكم' : 'Dashboard'}</Link>
            <Link to="/dashboard4/products">{language === 'ar' ? 'المنتجات' : 'Products'}</Link>
            <Link to="/dashboard4/orders">{language === 'ar' ? 'الطلبات' : 'Orders'}</Link>
            <Link to="/dashboard4/create-order">{language === 'ar' ? 'إنشاء طلب' : 'Create Order'}</Link>
            <Link to="/dashboard4/inventory">{language === 'ar' ? 'المخزون' : 'Inventory'}</Link>
            <Link to="/dashboard4/stats">{language === 'ar' ? 'الإحصائيات' : 'Stats'}</Link>
            <Link to="/dashboard4/settings" className="active">{language === 'ar' ? 'الإعدادات' : 'Settings'}</Link>
          </div>
        </div>

        {message.text && (
          <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-danger'}`}>
            {message.text}
          </div>
        )}

        {/* Settings Tabs */}
        <div className="settings-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="settings-tab-icon">{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* ============ GENERAL TAB ============ */}
        {activeTab === 'general' && (
          <div className="settings-content">
            {/* Store Visibility */}
            <div className="admin-card settings-card">
              <div className="settings-card-body" style={{ padding: 0 }}>
                <div className="settings-toggle-row">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <StoreIcon size="20px" style={{ color: '#6b7280' }} />
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b' }}>
                        {language === 'ar' ? 'فتح المتجر' : 'Store Open'}
                      </div>
                      <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                        {storeOpen
                          ? (language === 'ar' ? 'المتجر مفتوح ويظهر المنتجات للعملاء' : 'Store is open and showing products to customers')
                          : (language === 'ar' ? 'المتجر مغلق — لا تظهر المنتجات للعملاء' : 'Store is closed — products hidden from customers')
                        }
                      </div>
                    </div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={storeOpen}
                      onChange={() => handleToggle('storeOpen', storeOpen, setStoreOpen)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>

            {/* Show Email on Pay Page */}
            <div className="admin-card settings-card">
              <div className="settings-card-body" style={{ padding: 0 }}>
                <div className="settings-toggle-row">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <MailIcon size="20px" style={{ color: '#6b7280' }} />
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b' }}>
                        {language === 'ar' ? 'إظهار البريد الإلكتروني في صفحة الدفع' : 'Show Email on Pay Page'}
                      </div>
                      <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                        {showEmailOnPayPage
                          ? (language === 'ar' ? 'حقل البريد الإلكتروني يظهر للعملاء' : 'Email field is visible to customers')
                          : (language === 'ar' ? 'حقل البريد الإلكتروني مخفي من صفحة الدفع' : 'Email field is hidden from pay page')
                        }
                      </div>
                    </div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={showEmailOnPayPage}
                      onChange={() => handleToggle('showEmailOnPayPage', showEmailOnPayPage, setShowEmailOnPayPage)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>

            {/* Delivery Fee Options */}
            <div className="admin-card settings-card">
              <div className="settings-card-header">
                <div className="settings-card-title">
                  <TruckIcon size="20px" style={{ color: '#6b7280' }} />
                  <div>
                    <h3>{language === 'ar' ? 'خيارات التوصيل' : 'Delivery Options'}</h3>
                    <p>{language === 'ar' ? 'خيارات الرسوم المتاحة عند إنشاء الطلب (الأول هو الافتراضي)' : 'Fee options available when creating orders (first is default)'}</p>
                  </div>
                </div>
              </div>
              <div className="settings-card-body">
                {/* Current options */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                  {deliveryFeeOptions.map((fee, index) => (
                    <div key={index} style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '8px 14px', borderRadius: '20px',
                      background: index === 0 ? '#eff6ff' : '#f8fafc',
                      border: index === 0 ? '2px solid #3478f6' : '1px solid #e2e8f0',
                      fontSize: '14px', fontWeight: '600'
                    }}>
                      {editingFeeIndex === index ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <input
                            type="number"
                            inputMode="decimal"
                            value={editingFeeValue}
                            onChange={(e) => setEditingFeeValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') saveEditFee(); if (e.key === 'Escape') setEditingFeeIndex(null); }}
                            autoFocus
                            style={{ width: '60px', padding: '2px 6px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', textAlign: 'center' }}
                            min="0"
                          />
                          <button onClick={saveEditFee} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#22c55e', fontSize: '16px', padding: '2px' }}>✓</button>
                          <button onClick={() => setEditingFeeIndex(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '14px', padding: '2px' }}>✕</button>
                        </div>
                      ) : (
                        <>
                          <span onClick={() => startEditFee(index)} style={{ cursor: 'pointer', color: fee === 0 ? '#22c55e' : '#1e293b' }}>
                            {fee === 0 ? (language === 'ar' ? 'مجاني' : 'Free') : `AED ${fee}`}
                          </span>
                          {index === 0 && <span style={{ fontSize: '10px', color: '#3478f6', fontWeight: '700' }}>{language === 'ar' ? 'افتراضي' : 'DEFAULT'}</span>}
                          <button onClick={() => removeFeeOption(index)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '14px', padding: '0', lineHeight: 1 }}>✕</button>
                        </>
                      )}
                    </div>
                  ))}
                </div>

                {/* Add new option */}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <div style={{ position: 'relative', flex: 1, maxWidth: '150px' }}>
                    <input
                      type="number"
                      inputMode="decimal"
                      className="form-input"
                      placeholder={language === 'ar' ? 'مبلغ جديد' : 'New amount'}
                      value={newFeeValue}
                      onChange={(e) => setNewFeeValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') addFeeOption(); }}
                      style={{ paddingRight: '50px', fontSize: '14px' }}
                      min="0"
                    />
                    <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: '12px', fontWeight: '600' }}>AED</span>
                  </div>
                  <button onClick={addFeeOption} style={{ padding: '8px 16px', borderRadius: '20px', border: '1px dashed #cbd5e1', background: 'white', color: '#64748b', fontWeight: '600', fontSize: '13px', cursor: 'pointer' }}>
                    + {language === 'ar' ? 'إضافة' : 'Add'}
                  </button>
                  <button className="btn btn-primary" onClick={saveDeliveryFeeOptions} style={{ padding: '8px 20px', marginLeft: 'auto' }}>
                    {language === 'ar' ? 'حفظ' : 'Save'}
                  </button>
                </div>
              </div>
            </div>

            {/* Force Pay to Driver */}
            <div className="admin-card settings-card">
              <div className="settings-card-header">
                <div className="settings-card-title">
                  <TruckIcon size="20px" style={{ color: '#f59e0b' }} />
                  <div>
                    <h3>{language === 'ar' ? 'إظهار رسالة الدفع للسائق' : 'Show Pay to Driver Notice'}</h3>
                    <p>{language === 'ar' ? 'إظهار رسالة "غير شامل رسوم التوصيل" في صفحة الدفع لجميع الطلبات' : 'Show "delivery fee not included" notice on pay page for all orders'}</p>
                  </div>
                </div>
              </div>
              <div className="settings-card-body">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                      {language === 'ar' ? 'تفعيل لجميع الطلبات' : 'Enable for all orders'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
                      {language === 'ar' ? 'يرجى العلم بأنه غير شامل رسوم التوصيل - تُدفع مباشرة للسائق' : 'Delivery fee not included - pay directly to driver'}
                    </div>
                  </div>
                  <label className="toggle-switch" style={{ position: 'relative', display: 'inline-block', width: '50px', height: '26px', flexShrink: 0 }}>
                    <input
                      type="checkbox"
                      checked={forcePayToDriver}
                      onChange={async (e) => {
                        const val = e.target.checked;
                        setForcePayToDriver(val);
                        const success = await updateSetting('forcePayToDriver', String(val));
                        showMsg(success ? 'success' : 'error',
                          success
                            ? (language === 'ar' ? 'تم الحفظ' : 'Saved')
                            : (language === 'ar' ? 'فشل في الحفظ' : 'Failed')
                        );
                      }}
                      style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{
                      position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                      background: forcePayToDriver ? '#f59e0b' : '#e5e7eb',
                      borderRadius: '26px', transition: '0.3s'
                    }}>
                      <span style={{
                        position: 'absolute', content: '""', height: '20px', width: '20px',
                        left: forcePayToDriver ? '27px' : '3px', bottom: '3px',
                        background: 'white', borderRadius: '50%', transition: '0.3s',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                      }} />
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Email Language */}
            <div className="admin-card settings-card">
              <div className="settings-card-header">
                <div className="settings-card-title">
                  <MailIcon size="20px" style={{ color: '#6b7280' }} />
                  <div>
                    <h3>{language === 'ar' ? 'لغة الإيميلات' : 'Email Language'}</h3>
                    <p>{language === 'ar' ? 'لغة رسائل البريد للعملاء' : 'Language for customer emails'}</p>
                  </div>
                </div>
              </div>
              <div className="settings-card-body">
                <div style={{ display: 'flex', gap: '8px' }}>
                  {[
                    { val: 'ar', label: 'العربية AR' },
                    { val: 'en', label: 'EN English' }
                  ].map(opt => (
                    <button
                      key={opt.val}
                      onClick={async () => {
                        setEmailLanguage(opt.val);
                        await updateSetting('emailLanguage', opt.val);
                        showMsg('success', language === 'ar' ? 'تم تحديث لغة الإيميلات' : 'Email language updated');
                      }}
                      className={`settings-pill ${emailLanguage === opt.val ? 'active' : ''}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* WhatsApp Message Template */}
            <div className="admin-card settings-card">
              <div className="settings-card-header">
                <div className="settings-card-title">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#6b7280">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <div>
                    <h3>{language === 'ar' ? 'قالب رسالة واتساب' : 'WhatsApp Message Template'}</h3>
                    <p>{language === 'ar' ? 'رسالة واتساب التي ترسل مع رابط الدفع' : 'WhatsApp message sent with payment link'}</p>
                  </div>
                </div>
              </div>
              <div className="settings-card-body">
                <div style={{ marginBottom: '10px' }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    <span style={{ fontWeight: '600' }}>{language === 'ar' ? 'المتغيرات:' : 'Variables:'}</span>
                    {['{customerName}', '{orderId}', '{total}', '{paymentLink}'].map(v => (
                      <code key={v} style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', color: '#3478f6', cursor: 'pointer' }}
                        onClick={() => {
                          const el = document.getElementById('whatsapp-template-input');
                          if (el) {
                            const start = el.selectionStart;
                            const end = el.selectionEnd;
                            const text = whatsappTemplate;
                            setWhatsappTemplate(text.substring(0, start) + v + text.substring(end));
                            setTimeout(() => { el.focus(); el.setSelectionRange(start + v.length, start + v.length); }, 50);
                          }
                        }}
                      >{v}</code>
                    ))}
                  </div>
                  <textarea
                    id="whatsapp-template-input"
                    className="form-input"
                    value={whatsappTemplate}
                    onChange={(e) => setWhatsappTemplate(e.target.value)}
                    dir="rtl"
                    rows={8}
                    style={{ fontFamily: "'Cairo', 'Segoe UI', sans-serif", fontSize: '14px', lineHeight: '1.8', resize: 'vertical' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="btn btn-primary" style={{ flex: 1 }} onClick={async () => {
                    const success = await updateSetting('whatsappTemplate', whatsappTemplate);
                    showMsg(success ? 'success' : 'error',
                      success
                        ? (language === 'ar' ? 'تم حفظ قالب واتساب' : 'WhatsApp template saved')
                        : (language === 'ar' ? 'فشل في الحفظ' : 'Failed to save')
                    );
                  }}>
                    {language === 'ar' ? 'حفظ القالب' : 'Save Template'}
                  </button>
                  <button className="btn" style={{ background: '#f3f4f6', color: '#6b7280', border: 'none' }} onClick={() => {
                    setWhatsappTemplate(defaultWhatsAppTemplate);
                  }}>
                    {language === 'ar' ? 'استعادة' : 'Reset'}
                  </button>
                </div>
                {/* Preview */}
                <div style={{ marginTop: '12px', padding: '12px', background: '#dcf8c6', borderRadius: '10px', fontSize: '13px', lineHeight: '1.7', whiteSpace: 'pre-wrap', direction: 'rtl', fontFamily: "'Cairo', sans-serif" }}>
                  <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '4px', fontWeight: '600', textTransform: 'uppercase' }}>
                    {language === 'ar' ? 'معاينة' : 'Preview'}
                  </div>
                  {whatsappTemplate
                    .replace(/\{customerName\}/g, 'فاطمة')
                    .replace(/\{orderId\}/g, 'AE108242')
                    .replace(/\{total\}/g, '75')
                    .replace(/\{paymentLink\}/g, 'https://web.ae/pay/AE108242')
                  }
                </div>
              </div>
            </div>

            {/* WhatsApp Pay to Driver Template */}
            <div className="admin-card settings-card">
              <div className="settings-card-header">
                <div className="settings-card-title">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#f59e0b">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <div>
                    <h3 style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {language === 'ar' ? 'قالب واتساب - الدفع للسائق' : 'WhatsApp - Pay to Driver'}
                      <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '8px', background: '#fffbeb', color: '#b45309', fontWeight: '600', border: '1px solid #fcd34d' }}>🚚</span>
                    </h3>
                    <p>{language === 'ar' ? 'يُستخدم عند تفعيل الدفع للسائق' : 'Used when Pay to Driver is enabled'}</p>
                  </div>
                </div>
              </div>
              <div className="settings-card-body">
                <div style={{ marginBottom: '10px' }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    <span style={{ fontWeight: '600' }}>{language === 'ar' ? 'المتغيرات:' : 'Variables:'}</span>
                    {['{customerName}', '{orderId}', '{total}', '{deliveryFee}', '{paymentLink}'].map(v => (
                      <code key={v} style={{ background: v === '{deliveryFee}' ? '#fffbeb' : '#f1f5f9', padding: '1px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', color: v === '{deliveryFee}' ? '#b45309' : '#3478f6', cursor: 'pointer', border: v === '{deliveryFee}' ? '1px solid #fcd34d' : 'none' }}
                        onClick={() => {
                          const el = document.getElementById('whatsapp-driver-template-input');
                          if (el) {
                            const start = el.selectionStart;
                            const end = el.selectionEnd;
                            const text = whatsappDriverTemplate;
                            setWhatsappDriverTemplate(text.substring(0, start) + v + text.substring(end));
                            setTimeout(() => { el.focus(); el.setSelectionRange(start + v.length, start + v.length); }, 50);
                          }
                        }}
                      >{v}</code>
                    ))}
                  </div>
                  <textarea
                    id="whatsapp-driver-template-input"
                    className="form-input"
                    value={whatsappDriverTemplate}
                    onChange={(e) => setWhatsappDriverTemplate(e.target.value)}
                    dir="rtl"
                    rows={8}
                    style={{ fontFamily: "'Cairo', 'Segoe UI', sans-serif", fontSize: '14px', lineHeight: '1.8', resize: 'vertical' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="btn btn-primary" style={{ flex: 1, background: '#f59e0b', borderColor: '#f59e0b' }} onClick={async () => {
                    const success = await updateSetting('whatsappDriverTemplate', whatsappDriverTemplate);
                    showMsg(success ? 'success' : 'error',
                      success
                        ? (language === 'ar' ? 'تم حفظ قالب واتساب السائق' : 'Driver template saved')
                        : (language === 'ar' ? 'فشل في الحفظ' : 'Failed to save')
                    );
                  }}>
                    {language === 'ar' ? 'حفظ القالب' : 'Save Template'}
                  </button>
                  <button className="btn" style={{ background: '#f3f4f6', color: '#6b7280', border: 'none' }} onClick={() => {
                    setWhatsappDriverTemplate(defaultWhatsAppDriverTemplate);
                  }}>
                    {language === 'ar' ? 'استعادة' : 'Reset'}
                  </button>
                </div>
                {/* Preview */}
                <div style={{ marginTop: '12px', padding: '12px', background: '#dcf8c6', borderRadius: '10px', fontSize: '13px', lineHeight: '1.7', whiteSpace: 'pre-wrap', direction: 'rtl', fontFamily: "'Cairo', sans-serif", border: '2px solid #fcd34d' }}>
                  <div style={{ fontSize: '10px', color: '#92400e', marginBottom: '4px', fontWeight: '600', textTransform: 'uppercase' }}>
                    {language === 'ar' ? 'معاينة - الدفع للسائق' : 'Preview - Pay to Driver'}
                  </div>
                  {whatsappDriverTemplate
                    .replace(/\{customerName\}/g, 'فاطمة')
                    .replace(/\{orderId\}/g, 'AE108242')
                    .replace(/\{total\}/g, '75')
                    .replace(/\{deliveryFee\}/g, '35')
                    .replace(/\{paymentLink\}/g, 'https://web.ae/pay/AE108242')
                  }
                </div>
              </div>
            </div>

            {/* Email Templates */}
            <div className="admin-card settings-card">
              <div className="settings-card-header">
                <div className="settings-card-title">
                  <MailIcon size="20px" style={{ color: '#6b7280' }} />
                  <div>
                    <h3>{language === 'ar' ? 'قوالب الإيميلات' : 'Email Templates'}</h3>
                    <p>{language === 'ar' ? 'تعديل نصوص إيميلات الطلبات' : 'Edit order email text content'}</p>
                  </div>
                </div>
              </div>
              <div className="settings-card-body">
                {/* Toggle between Confirmation / Shipped */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                  {[
                    { id: 'confirm', label: language === 'ar' ? 'تأكيد الطلب' : 'Order Confirmation' },
                    { id: 'shipped', label: language === 'ar' ? 'تم الشحن' : 'Shipped' }
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setEmailTemplateTab(tab.id)}
                      className={`settings-pill ${emailTemplateTab === tab.id ? 'active' : ''}`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {emailTemplateTab === 'confirm' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    {/* Arabic fields */}
                    <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '14px', border: '1px solid #f3f4f6' }}>
                      <div style={{ fontSize: '12px', fontWeight: '700', color: '#6b7280', marginBottom: '10px', textTransform: 'uppercase' }}>العربية (Arabic)</div>
                      <div className="form-group" style={{ marginBottom: '10px' }}>
                        <label className="form-label" style={{ fontSize: '12px' }}>{language === 'ar' ? 'العنوان' : 'Title'}</label>
                        <input type="text" className="form-input" dir="rtl" value={emailTemplates.confirmTitleAr}
                          onChange={(e) => setEmailTemplates(prev => ({ ...prev, confirmTitleAr: e.target.value }))} />
                      </div>
                      <div className="form-group" style={{ marginBottom: '10px' }}>
                        <label className="form-label" style={{ fontSize: '12px' }}>{language === 'ar' ? 'العنوان الفرعي' : 'Subtitle'}</label>
                        <input type="text" className="form-input" dir="rtl" value={emailTemplates.confirmSubtitleAr}
                          onChange={(e) => setEmailTemplates(prev => ({ ...prev, confirmSubtitleAr: e.target.value }))} />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label" style={{ fontSize: '12px' }}>{language === 'ar' ? 'التنبيه' : 'Disclaimer'}</label>
                        <textarea className="form-input" dir="rtl" rows={3} value={emailTemplates.confirmDisclaimerAr}
                          onChange={(e) => setEmailTemplates(prev => ({ ...prev, confirmDisclaimerAr: e.target.value }))}
                          style={{ resize: 'vertical', lineHeight: '1.6' }} />
                      </div>
                    </div>
                    {/* English fields */}
                    <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '14px', border: '1px solid #f3f4f6' }}>
                      <div style={{ fontSize: '12px', fontWeight: '700', color: '#6b7280', marginBottom: '10px', textTransform: 'uppercase' }}>English</div>
                      <div className="form-group" style={{ marginBottom: '10px' }}>
                        <label className="form-label" style={{ fontSize: '12px' }}>Title</label>
                        <input type="text" className="form-input" value={emailTemplates.confirmTitleEn}
                          onChange={(e) => setEmailTemplates(prev => ({ ...prev, confirmTitleEn: e.target.value }))} />
                      </div>
                      <div className="form-group" style={{ marginBottom: '10px' }}>
                        <label className="form-label" style={{ fontSize: '12px' }}>Subtitle</label>
                        <input type="text" className="form-input" value={emailTemplates.confirmSubtitleEn}
                          onChange={(e) => setEmailTemplates(prev => ({ ...prev, confirmSubtitleEn: e.target.value }))} />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label" style={{ fontSize: '12px' }}>Disclaimer</label>
                        <textarea className="form-input" rows={3} value={emailTemplates.confirmDisclaimerEn}
                          onChange={(e) => setEmailTemplates(prev => ({ ...prev, confirmDisclaimerEn: e.target.value }))}
                          style={{ resize: 'vertical', lineHeight: '1.6' }} />
                      </div>
                    </div>
                  </div>
                )}

                {emailTemplateTab === 'shipped' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '14px', border: '1px solid #f3f4f6' }}>
                      <div style={{ fontSize: '12px', fontWeight: '700', color: '#6b7280', marginBottom: '10px', textTransform: 'uppercase' }}>العربية (Arabic)</div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label" style={{ fontSize: '12px' }}>{language === 'ar' ? 'العنوان' : 'Title'}</label>
                        <input type="text" className="form-input" dir="rtl" value={emailTemplates.shippedTitleAr}
                          onChange={(e) => setEmailTemplates(prev => ({ ...prev, shippedTitleAr: e.target.value }))} />
                      </div>
                    </div>
                    <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '14px', border: '1px solid #f3f4f6' }}>
                      <div style={{ fontSize: '12px', fontWeight: '700', color: '#6b7280', marginBottom: '10px', textTransform: 'uppercase' }}>English</div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label" style={{ fontSize: '12px' }}>Title</label>
                        <input type="text" className="form-input" value={emailTemplates.shippedTitleEn}
                          onChange={(e) => setEmailTemplates(prev => ({ ...prev, shippedTitleEn: e.target.value }))} />
                      </div>
                    </div>
                  </div>
                )}

                {/* Footer (shared) */}
                <div style={{ marginTop: '14px' }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" style={{ fontSize: '12px' }}>{language === 'ar' ? 'نص الفوتر' : 'Footer Text'}</label>
                    <input type="text" className="form-input" value={emailTemplates.footerText}
                      onChange={(e) => setEmailTemplates(prev => ({ ...prev, footerText: e.target.value }))}
                      placeholder="Web.ae Shop" />
                  </div>
                </div>

                {/* Save / Reset */}
                <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
                  <button className="btn btn-primary" style={{ flex: 1 }} onClick={async () => {
                    const success = await updateSetting('emailTemplates', JSON.stringify(emailTemplates));
                    showMsg(success ? 'success' : 'error',
                      success
                        ? (language === 'ar' ? 'تم حفظ قوالب الإيميلات' : 'Email templates saved')
                        : (language === 'ar' ? 'فشل في الحفظ' : 'Failed to save')
                    );
                  }}>
                    {language === 'ar' ? 'حفظ القوالب' : 'Save Templates'}
                  </button>
                  <button className="btn" style={{ background: '#f3f4f6', color: '#6b7280', border: 'none' }} onClick={() => {
                    setEmailTemplates({ ...defaultEmailTemplates });
                  }}>
                    {language === 'ar' ? 'استعادة' : 'Reset'}
                  </button>
                </div>

                {/* Preview */}
                <div style={{ marginTop: '14px', border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden' }}>
                  <div style={{ fontSize: '10px', color: '#6b7280', padding: '6px 12px', fontWeight: '600', textTransform: 'uppercase', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                    {language === 'ar' ? 'معاينة' : 'Preview'} — {emailTemplateTab === 'confirm'
                      ? (language === 'ar' ? 'تأكيد الطلب' : 'Order Confirmation')
                      : (language === 'ar' ? 'تم الشحن' : 'Shipped')}
                  </div>
                  <div style={{ background: '#ffffff', padding: '24px 20px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', marginBottom: emailTemplateTab === 'confirm' ? '4px' : 0 }}>
                      {emailTemplateTab === 'confirm' ? emailTemplates.confirmTitleAr : emailTemplates.shippedTitleAr}
                    </div>
                    {emailTemplateTab === 'confirm' && (
                      <div style={{ fontSize: '14px', color: '#64748b' }}>{emailTemplates.confirmSubtitleAr}</div>
                    )}
                  </div>
                  {emailTemplateTab === 'confirm' && (
                    <div style={{ background: '#fef3c7', padding: '10px 20px', borderBottom: '1px solid #fcd34d', fontSize: '12px', color: '#92400e', lineHeight: '1.6', textAlign: 'center', direction: 'rtl' }}>
                      {emailTemplates.confirmDisclaimerAr}
                    </div>
                  )}
                  <div style={{ padding: '16px 20px', fontSize: '12px', color: '#9ca3af' }}>
                    <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '10px', marginBottom: '8px', color: '#6b7280', textAlign: 'center' }}>
                      [{language === 'ar' ? 'تفاصيل الطلب والمنتجات' : 'Order details & items'}]
                    </div>
                  </div>
                  <div style={{ background: '#f8fafc', padding: '12px 20px', textAlign: 'center', borderTop: '1px solid #e5e7eb', fontSize: '11px', color: '#94a3b8' }}>
                    © {new Date().getFullYear()} {emailTemplates.footerText}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ============ PAYMENT TAB ============ */}
        {activeTab === 'payment' && (
          <div className="settings-content">
            {/* Payment Methods */}
            <div className="admin-card settings-card">
              <div className="settings-card-header">
                <div className="settings-card-title">
                  <CreditCardIcon size="20px" style={{ color: '#6b7280' }} />
                  <div>
                    <h3>{language === 'ar' ? 'طرق الدفع' : 'Payment Methods'}</h3>
                    <p>{language === 'ar' ? 'تفعيل أو تعطيل طرق الدفع' : 'Enable or disable payment methods'}</p>
                  </div>
                </div>
              </div>
              <div className="settings-card-body" style={{ padding: 0 }}>
                {[
                  { icon: <DollarIcon size="20px" style={{ color: '#6b7280' }} />, key: 'codEnabled', val: codEnabled, setter: setCodEnabled, label: language === 'ar' ? 'الدفع عند الاستلام' : 'Cash on Delivery', desc: language === 'ar' ? 'الدفع نقداً عند التوصيل' : 'Pay cash on delivery' },
                  { icon: <BankIcon size="20px" style={{ color: '#6b7280' }} />, key: 'bankTransferEnabled', val: bankTransferEnabled, setter: setBankTransferEnabled, label: language === 'ar' ? 'التحويل البنكي' : 'Bank Transfer', desc: language === 'ar' ? 'الدفع عبر التحويل البنكي' : 'Pay via bank transfer' },
                  { icon: <CreditCardIcon size="20px" style={{ color: '#6b7280' }} />, key: 'creditCardEnabled', val: creditCardEnabled, setter: setCreditCardEnabled, label: language === 'ar' ? 'الدفع بالبطاقة' : 'Credit/Debit Card', desc: 'Stripe' }
                ].map((method, i) => (
                  <div key={method.key} className="settings-toggle-row" style={{ borderTop: i > 0 ? '1px solid #f3f4f6' : 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                      <span style={{ fontSize: '20px', display: 'flex' }}>{method.icon}</span>
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b' }}>{method.label}</div>
                        <div style={{ fontSize: '12px', color: '#9ca3af' }}>{method.desc}</div>
                      </div>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={method.val}
                        onChange={() => handleToggle(method.key, method.val, method.setter)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Bank Details */}
            <div className="admin-card settings-card">
              <div className="settings-card-header">
                <div className="settings-card-title">
                  <BankIcon size="20px" style={{ color: '#6b7280' }} />
                  <div>
                    <h3>{language === 'ar' ? 'تفاصيل الحساب البنكي' : 'Bank Account Details'}</h3>
                    <p>{language === 'ar' ? 'معلومات التحويل البنكي للعملاء' : 'Bank transfer info shown to customers'}</p>
                  </div>
                </div>
              </div>
              <div className="settings-card-body">
                <div className="settings-form-row">
                  <div className="form-group">
                    <label className="form-label">{language === 'ar' ? 'اسم البنك (EN)' : 'Bank Name (EN)'}</label>
                    <input type="text" className="form-input" value={bankDetails.bankName}
                      onChange={(e) => handleBankDetailChange('bankName', e.target.value)} placeholder="Emirates NBD" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{language === 'ar' ? 'اسم البنك (AR)' : 'Bank Name (AR)'}</label>
                    <input type="text" className="form-input" value={bankDetails.bankNameAr}
                      onChange={(e) => handleBankDetailChange('bankNameAr', e.target.value)} placeholder="الإمارات دبي الوطني" dir="rtl" />
                  </div>
                </div>

                <div className="settings-form-row">
                  <div className="form-group">
                    <label className="form-label">{language === 'ar' ? 'اسم الحساب (EN)' : 'Account Name (EN)'}</label>
                    <input type="text" className="form-input" value={bankDetails.accountName}
                      onChange={(e) => handleBankDetailChange('accountName', e.target.value)} placeholder="Company Name LLC" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{language === 'ar' ? 'اسم الحساب (AR)' : 'Account Name (AR)'}</label>
                    <input type="text" className="form-input" value={bankDetails.accountNameAr}
                      onChange={(e) => handleBankDetailChange('accountNameAr', e.target.value)} placeholder="اسم الشركة ذ.م.م" dir="rtl" />
                  </div>
                </div>

                <div className="settings-form-row">
                  <div className="form-group">
                    <label className="form-label">IBAN</label>
                    <input type="text" className="form-input" value={bankDetails.iban}
                      onChange={(e) => handleBankDetailChange('iban', e.target.value)}
                      placeholder="AE12 0260 0010 1234 5678 901" style={{ fontFamily: 'monospace' }} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{language === 'ar' ? 'رقم الحساب' : 'Account Number'}</label>
                    <input type="text" className="form-input" value={bankDetails.accountNumber}
                      onChange={(e) => handleBankDetailChange('accountNumber', e.target.value)}
                      placeholder="1234567890" style={{ fontFamily: 'monospace' }} />
                  </div>
                </div>

                <button className="btn btn-primary" onClick={saveBankDetails} disabled={saving} style={{ width: '100%', marginTop: '8px' }}>
                  {saving ? (language === 'ar' ? 'جاري الحفظ...' : 'Saving...') : (language === 'ar' ? 'حفظ التفاصيل البنكية' : 'Save Bank Details')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ============ PROMO TAB ============ */}
        {activeTab === 'promo' && (
          <div className="settings-content">
            <div className="admin-card settings-card">
              <div className="settings-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="settings-card-title">
                  <TagIcon size="20px" style={{ color: '#6b7280' }} />
                  <div>
                    <h3>{language === 'ar' ? 'رموز الخصم' : 'Promo Codes'}</h3>
                    <p>{language === 'ar' ? 'إدارة رموز الخصم والعروض' : 'Manage discount codes and offers'}</p>
                  </div>
                </div>
                <button
                  className={`btn ${showAddPromo ? '' : 'btn-primary'}`}
                  style={{ padding: '8px 16px', fontSize: '13px', background: showAddPromo ? '#f3f4f6' : '', color: showAddPromo ? '#6b7280' : '' }}
                  onClick={() => setShowAddPromo(!showAddPromo)}
                >
                  {showAddPromo ? (language === 'ar' ? 'إلغاء' : 'Cancel') : (language === 'ar' ? '+ إضافة' : '+ Add')}
                </button>
              </div>

              {promoMessage.text && (
                <div style={{
                  padding: '10px 16px', margin: '0 20px 16px', borderRadius: '8px',
                  background: promoMessage.type === 'success' ? '#d1fae5' : '#fee2e2',
                  color: promoMessage.type === 'success' ? '#065f46' : '#dc2626',
                  fontSize: '13px', fontWeight: '500'
                }}>
                  {promoMessage.text}
                </div>
              )}

              {showAddPromo && (
                <div className="settings-card-body" style={{ borderBottom: '1px solid #e5e7eb', background: '#fafbfc' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: '12px' }}>{language === 'ar' ? 'الرمز' : 'Code'}</label>
                      <input type="text" className="form-input" value={newPromo.code}
                        onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value.toUpperCase() })}
                        placeholder="SAVE10" dir="ltr" style={{ fontFamily: 'monospace', fontWeight: '700' }} />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: '12px' }}>{language === 'ar' ? 'النوع' : 'Type'}</label>
                      <select className="form-input" value={newPromo.discountType}
                        onChange={(e) => setNewPromo({ ...newPromo, discountType: e.target.value })}>
                        <option value="percentage">% {language === 'ar' ? 'نسبة' : 'Percent'}</option>
                        <option value="fixed">{language === 'ar' ? 'د.إ ثابت' : 'AED Fixed'}</option>
                      </select>
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: '12px' }}>
                        {newPromo.discountType === 'percentage' ? (language === 'ar' ? 'النسبة %' : 'Value %') : (language === 'ar' ? 'المبلغ AED' : 'Amount AED')}
                      </label>
                      <input type="number" inputMode="decimal" className="form-input" value={newPromo.discountValue}
                        onChange={(e) => setNewPromo({ ...newPromo, discountValue: e.target.value })}
                        placeholder={newPromo.discountType === 'percentage' ? '10' : '20'} />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: '12px' }}>{language === 'ar' ? 'أقصى خصم AED' : 'Max Discount AED'}</label>
                      <input type="number" inputMode="decimal" className="form-input" value={newPromo.maxDiscount}
                        onChange={(e) => setNewPromo({ ...newPromo, maxDiscount: e.target.value })}
                        placeholder={language === 'ar' ? 'بدون حد' : 'No limit'} />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: '12px' }}>{language === 'ar' ? 'الحد الأدنى AED' : 'Min Order AED'}</label>
                      <input type="number" inputMode="decimal" className="form-input" value={newPromo.minOrder}
                        onChange={(e) => setNewPromo({ ...newPromo, minOrder: e.target.value })}
                        placeholder="0" />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: '12px' }}>{language === 'ar' ? 'أقصى استخدام' : 'Max Uses'}</label>
                      <input type="number" inputMode="numeric" className="form-input" value={newPromo.maxUses}
                        onChange={(e) => setNewPromo({ ...newPromo, maxUses: e.target.value })}
                        placeholder="∞" />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: '12px' }}>{language === 'ar' ? 'الانتهاء' : 'Expires'}</label>
                      <input type="date" className="form-input" value={newPromo.expiresAt}
                        onChange={(e) => setNewPromo({ ...newPromo, expiresAt: e.target.value })} />
                    </div>
                  </div>
                  <button className="btn btn-primary" onClick={handleAddPromo} style={{ marginTop: '14px', width: '100%' }}>
                    {language === 'ar' ? 'إضافة رمز الخصم' : 'Add Promo Code'}
                  </button>
                </div>
              )}

              <div className="settings-card-body" style={{ padding: 0 }}>
                {loadingPromos ? (
                  <div style={{ padding: '30px', textAlign: 'center' }}>
                    <div className="spinner" style={{ margin: '0 auto' }}></div>
                  </div>
                ) : promoCodes.length === 0 ? (
                  <div style={{ padding: '40px 20px', textAlign: 'center', color: '#9ca3af' }}>
                    <div style={{ marginBottom: '8px' }}><TagIcon size="32px" style={{ color: '#9ca3af' }} /></div>
                    {language === 'ar' ? 'لا توجد رموز خصم بعد' : 'No promo codes yet'}
                  </div>
                ) : (
                  <div>
                    {promoCodes.map((promo, i) => (
                      <div key={promo.id} className="settings-toggle-row" style={{ borderTop: i > 0 ? '1px solid #f3f4f6' : 'none' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <span style={{ fontFamily: 'monospace', fontWeight: '700', fontSize: '14px', color: '#1e293b', background: '#f1f5f9', padding: '2px 10px', borderRadius: '6px' }}>
                              {promo.code}
                            </span>
                            <span style={{ fontSize: '13px', fontWeight: '600', color: promo.discountType === 'percentage' ? '#7c3aed' : '#059669' }}>
                              {promo.discountType === 'percentage' ? `${promo.discountValue}%` : <AedPrice amount={promo.discountValue} />}
                            </span>
                            {promo.maxDiscount > 0 && (
                              <span style={{ fontSize: '11px', color: '#f59e0b', fontWeight: '600', background: '#fef3c7', padding: '1px 6px', borderRadius: '4px' }}>
                                {language === 'ar' ? <span>حد <AedPrice amount={promo.maxDiscount} /></span> : <span>Max <AedPrice amount={promo.maxDiscount} /></span>}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                            <span>{promo.usedCount}{promo.maxUses ? `/${promo.maxUses}` : ''} {language === 'ar' ? 'استخدام' : 'uses'}</span>
                            {promo.minOrder > 0 && <span>{language === 'ar' ? <span>حد أدنى <AedPrice amount={promo.minOrder} /></span> : <span>Min <AedPrice amount={promo.minOrder} /></span>}</span>}
                            {promo.expiresAt && <span>{language === 'ar' ? `ينتهي ${promo.expiresAt}` : `Exp ${promo.expiresAt}`}</span>}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                          <label className="toggle-switch">
                            <input type="checkbox" checked={promo.active} onChange={() => togglePromoActive(promo)} />
                            <span className="toggle-slider"></span>
                          </label>
                          <button onClick={() => deletePromo(promo.id)}
                            style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px', display: 'flex' }}>
                            <TrashIcon size="16px" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ============ USERS TAB ============ */}
        {activeTab === 'users' && (
          <div className="settings-content">
            <div className="admin-card settings-card">
              <div className="settings-card-header">
                <div className="settings-card-title">
                  <UsersIcon size="20px" style={{ color: '#6b7280' }} />
                  <div>
                    <h3>{language === 'ar' ? 'المستخدمين' : 'Users'}</h3>
                    <p>
                      {users.length} {language === 'ar' ? 'مستخدم' : 'users'} — {users.filter(u => u.role === 'admin').length} {language === 'ar' ? 'مدير' : 'admins'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="settings-card-body" style={{ padding: 0 }}>
                {loadingUsers ? (
                  <div style={{ padding: '30px', textAlign: 'center' }}>
                    <div className="spinner" style={{ margin: '0 auto' }}></div>
                  </div>
                ) : users.length === 0 ? (
                  <div style={{ padding: '40px 20px', textAlign: 'center', color: '#9ca3af' }}>
                    {language === 'ar' ? 'لا يوجد مستخدمون' : 'No users found'}
                  </div>
                ) : users.length > 10 ? (
                  <div style={{ padding: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
                    <div>
                      <div style={{ fontSize: '28px', fontWeight: '700', color: 'var(--primary)' }}>{users.length}</div>
                      <div style={{ color: '#6b7280', fontSize: '13px' }}>
                        {language === 'ar' ? 'مستخدم مسجل' : 'Registered Users'}
                      </div>
                    </div>
                    <Link to="/dashboard4/users" className="btn btn-primary" style={{ padding: '10px 20px' }}>
                      {language === 'ar' ? 'إدارة المستخدمين →' : 'Manage Users →'}
                    </Link>
                  </div>
                ) : (
                  <div>
                    {users.map((user, i) => (
                      <div key={user.id} className="settings-toggle-row" style={{ borderTop: i > 0 ? '1px solid #f3f4f6' : 'none' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b' }}>{user.name}</span>
                            <span style={{
                              fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '10px',
                              background: user.role === 'admin' ? '#dbeafe' : '#f3f4f6',
                              color: user.role === 'admin' ? '#1d4ed8' : '#6b7280'
                            }}>
                              {user.role === 'admin' ? (language === 'ar' ? 'مدير' : 'Admin') : (language === 'ar' ? 'مستخدم' : 'User')}
                            </span>
                          </div>
                          <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>{user.email}</div>
                        </div>
                        <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                          <button onClick={() => openEditUser(user)}
                            style={{ padding: '6px 16px', fontSize: '12px', fontWeight: '600', background: 'var(--primary)', color: 'white', border: 'none', borderRadius: '25px', cursor: 'pointer' }}>
                            {language === 'ar' ? 'تعديل' : 'Edit'}
                          </button>
                          <button onClick={() => deleteUser(user.id)}
                            style={{ padding: '6px 16px', fontSize: '12px', fontWeight: '600', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '25px', cursor: 'pointer' }}>
                            {language === 'ar' ? 'حذف' : 'Delete'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {editingUser && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: '20px'
          }}>
            <div style={{
              background: 'white', borderRadius: '16px', padding: '24px',
              width: '100%', maxWidth: '420px', maxHeight: '90vh', overflow: 'auto'
            }}>
              <h3 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: '700' }}>
                {language === 'ar' ? 'تعديل المستخدم' : 'Edit User'}
              </h3>

              {userMessage.text && (
                <div style={{
                  padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px',
                  background: userMessage.type === 'success' ? '#d1fae5' : userMessage.type === 'error' ? '#fee2e2' : '#e0f2fe',
                  color: userMessage.type === 'success' ? '#065f46' : userMessage.type === 'error' ? '#dc2626' : '#0369a1'
                }}>
                  {userMessage.text}
                </div>
              )}

              <div className="form-group" style={{ marginBottom: '14px' }}>
                <label className="form-label">{language === 'ar' ? 'الاسم' : 'Name'}</label>
                <input type="text" className="form-input" value={editUserData.name}
                  onChange={(e) => setEditUserData({ ...editUserData, name: e.target.value })} />
              </div>

              <div className="form-group" style={{ marginBottom: '14px' }}>
                <label className="form-label">{language === 'ar' ? 'البريد الإلكتروني' : 'Email'}</label>
                <input type="email" className="form-input" value={editUserData.email}
                  onChange={(e) => setEditUserData({ ...editUserData, email: e.target.value })} />
              </div>

              <div className="form-group" style={{ marginBottom: '14px' }}>
                <label className="form-label">
                  {language === 'ar' ? 'كلمة المرور الجديدة' : 'New Password'}
                  <span style={{ color: '#9ca3af', fontSize: '11px', marginLeft: '6px' }}>
                    ({language === 'ar' ? 'اختياري' : 'optional'})
                  </span>
                </label>
                <input type="password" className="form-input" value={editUserData.password}
                  onChange={(e) => setEditUserData({ ...editUserData, password: e.target.value })}
                  placeholder="••••••" />
              </div>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label className="form-label">{language === 'ar' ? 'الدور' : 'Role'}</label>
                <select className="form-input" value={editUserData.role}
                  onChange={(e) => setEditUserData({ ...editUserData, role: e.target.value })}>
                  <option value="user">{language === 'ar' ? 'مستخدم' : 'User'}</option>
                  <option value="admin">{language === 'ar' ? 'مدير' : 'Admin'}</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button className="btn" onClick={closeEditUser}
                  style={{ flex: 1, background: '#f3f4f6', color: '#374151', border: 'none' }}>
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </button>
                <button className="btn btn-primary" onClick={saveUser} style={{ flex: 1 }}>
                  {language === 'ar' ? 'حفظ' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
